import type { HTMLAttributes, Ref } from "react";

import {
  clusterAlign,
  clusterGap,
  clusterJustify,
} from "~/components/Cluster.css";

export type ClusterGap = "none" | "xs" | "sm" | "md" | "lg" | "xl";
export type ClusterAlign = "start" | "center" | "end" | "baseline";
export type ClusterJustify = "start" | "center" | "end" | "between";

export interface ClusterProps extends HTMLAttributes<HTMLDivElement> {
  /** Gap between children. Defaults to "md" (16px). */
  gap?: ClusterGap;
  /** Cross-axis alignment. Defaults to "center". */
  align?: ClusterAlign;
  /** Main-axis justification. Defaults to "start". */
  justify?: ClusterJustify;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Horizontal layout primitive that wraps. Renders a <div> with flex
 * row layout, token-driven gap, and configurable alignment. Children
 * flow left-to-right and wrap to a new line when they run out of
 * horizontal space — perfect for toolbars, tag clouds, badge lists.
 */
export function Cluster({
  gap = "md",
  align = "center",
  justify = "start",
  className,
  ref,
  ...rest
}: ClusterProps) {
  const variant = `${clusterGap[gap]} ${clusterAlign[align]} ${clusterJustify[justify]}`;
  const merged =
    className !== undefined && className.length > 0
      ? `${variant} ${className}`
      : variant;
  return <div ref={ref} className={merged} {...rest} />;
}
